// Fal.ai Image Generation Service
// This service handles image generation using fal.ai API
//
// Phase 3 C2 (2026-05-26): dodany X-Workspace header zeby nginx /api/fal-ai/
// wybralo poprawny klucz per workspace (FAL_AI_API_KEY_FIRMA / _SPONSORAKI).
// Frontend NIE wysyla Authorization — nginx wstrzykuje (ma to dotad).

import { getActiveWorkspace } from "@/lib/workspace";

export interface FalAIConfig {
  apiKey: string;
  baseUrl?: string;
}

export interface ImageGenerationOptions {
  prompt: string;
  model?: string;
  imageSize?: string;
  numImages?: number;
  guidanceScale?: number;
  numInferenceSteps?: number;
  /** Negative prompt — co MA NIE BYC w obrazie (np. "text, infographic, diagram").
   *  flux-pro/v1.1 i flux-realism wspieraja; flux/schnell ignoruje (za malo steps). */
  negativePrompt?: string;
}

export interface GeneratedImage {
  url: string;
  width?: number;
  height?: number;
  contentType?: string;
}

export interface FalAIResponse {
  success: boolean;
  images?: GeneratedImage[];
  error?: string;
}

class FalAIService {
  private config: FalAIConfig | null = null;
  private readonly defaultBaseUrl = 'https://fal.run';

  // Available models and their configurations
  private readonly models = {
    'fal-ai/flux-pro/v1.1': {
      imageSize: 'landscape_16_9',
      numInferenceSteps: 28,
      guidanceScale: 3.5,
      safetyTolerance: 6
    },
    'fal-ai/flux/schnell': {
      imageSize: 'landscape_16_9',
      numInferenceSteps: 4,
      enableSafetyChecker: true
    },
    'fal-ai/flux-realism': {
      imageSize: 'landscape_16_9',
      numInferenceSteps: 28,
      guidanceScale: 3.5
    },
    'fal-ai/fast-sdxl': {
      imageSize: 'landscape_16_9',
      negativePrompt: 'low quality, blurry, distorted, deformed',
      guidanceScale: 7.5,
      numInferenceSteps: 25
    },
    'fal-ai/flux-pro/v1.1-ultra': {
      imageSize: 'landscape_16_9',
      numInferenceSteps: 28,
      guidanceScale: 3.5,
      safetyTolerance: 6,
      aspectRatio: '16:9'
    }
  };

  setConfig(config: FalAIConfig) {
    this.config = config;
  }

  private getApiUrl(model: string): string {
    const baseUrl = this.config?.baseUrl || this.defaultBaseUrl;
    return `${baseUrl}/${model}`;
  }

  private getModelConfig(model: string) {
    return this.models[model as keyof typeof this.models] || this.models['fal-ai/flux-pro/v1.1'];
  }

  async generateImage(options: ImageGenerationOptions): Promise<FalAIResponse> {
    console.log('Fal.ai generateImage called with:', {
      prompt: options.prompt,
      promptType: typeof options.prompt,
      promptLength: options.prompt?.length || 0
    });

    // Client-side apiKey guard usunięty (2026-04-22) — nginx proxy (nginx.conf:137-148)
    // nadpisuje Authorization header z env FAL_AI_API_KEY. Browser nie potrzebuje klucza
    // w bundle. Gdy apiKey pusty tu → nginx doklepuje → FAL dostaje request.

    // Validate prompt
    if (!options.prompt || typeof options.prompt !== 'string' || options.prompt.trim().length === 0) {
      console.error('Invalid prompt for Fal.ai:', options.prompt);
      return {
        success: false,
        error: 'Invalid or empty prompt provided'
      };
    }

    const model = options.model || 'fal-ai/fast-sdxl';

    // Use proxy in both development and production
    const apiUrl = `/api/fal-ai/${model}`;

    const modelConfig = this.getModelConfig(model);

    try {
      // Prepare request payload (modelConfig spread FIRST, then explicit overrides
      // — wczesniej spread bylo NA KONCU co nadpisywalo explicit values; bug fix.)
      const payload: Record<string, unknown> = {
        ...modelConfig, // model-specific defaults (numInferenceSteps, guidanceScale, ...)
        prompt: options.prompt,
        num_images: options.numImages || 1,
        image_size: options.imageSize || modelConfig.imageSize,
        guidance_scale: options.guidanceScale || (modelConfig as any).guidanceScale,
        num_inference_steps: options.numInferenceSteps || (modelConfig as any).numInferenceSteps,
      };

      // Negative prompt — flux-pro/v1.1 + flux-realism + fast-sdxl wspieraja.
      // Naming convention FAL: snake_case `negative_prompt`.
      if (options.negativePrompt) {
        payload.negative_prompt = options.negativePrompt;
      } else if ((modelConfig as any).negativePrompt) {
        payload.negative_prompt = (modelConfig as any).negativePrompt;
      }

      // safety_tolerance — FAL oczekuje snake_case stringa "1"-"6" (1=ostry, 6=luzny).
      // Default FAL "2" blokuje tematy medyczne (witamina D, niedobor, objawy, bol)
      // -> zwraca czarny/ocenzurowany obraz. Spread daje camelCase ktory FAL IGNORUJE,
      // wiec mapujemy recznie + kasujemy camelCase. Dotyczy flux-pro/v1.1 (+ ultra).
      if ((modelConfig as any).safetyTolerance != null) {
        payload.safety_tolerance = String((modelConfig as any).safetyTolerance);
      }
      delete (payload as any).safetyTolerance;

      console.log('Generating image with fal.ai:', {
        model,
        prompt: options.prompt.substring(0, 100) + '...',
        payload,
        apiUrl
      });

      // Prepare headers — nginx /api/fal-ai/ wstrzykuje Authorization Key
      // per X-Workspace mapowanie (FAL_AI_API_KEY_FIRMA / _SPONSORAKI).
      // Frontend Authorization zachowany jako Key + this.config.apiKey (pusty
      // w browser, bo VITE_FAL_AI_API_KEY juz nie istnieje — nginx nadpisuje).
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'Authorization': `Key ${this.config?.apiKey || ''}`,  // ✅ "Key" nie "Bearer" dla Fal AI!
        'X-Workspace': getActiveWorkspace(),
      };

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Fal.ai API error:', response.status, errorText);

        return {
          success: false,
          error: `Fal.ai API error: ${response.status} - ${errorText}`
        };
      }

      const result = await response.json();
      console.log('Fal.ai response:', result);

      // Handle different response formats
      if (result.images && Array.isArray(result.images)) {
        const images: GeneratedImage[] = result.images.map((img: any) => ({
          url: img.url,
          width: img.width,
          height: img.height,
          contentType: img.content_type
        }));

        return {
          success: true,
          images
        };
      }

      // Fallback for single image response
      if (result.url) {
        return {
          success: true,
          images: [{
            url: result.url,
            width: result.width,
            height: result.height,
            contentType: result.content_type
          }]
        };
      }

      return {
        success: false,
        error: 'Unexpected response format from fal.ai'
      };

    } catch (error) {
      console.error('Error generating image with fal.ai:', error);

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  async generateImagesBatch(
    prompts: string[],
    options: Omit<ImageGenerationOptions, 'prompt'> = {}
  ): Promise<FalAIResponse[]> {
    const results: FalAIResponse[] = [];

    for (const prompt of prompts) {
      const result = await this.generateImage({
        ...options,
        prompt
      });
      results.push(result);

      // Small delay between requests to avoid rate limiting
      if (prompts.length > 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    return results;
  }

  // Test connection to fal.ai
  async testConnection(): Promise<FalAIResponse> {
    const testPrompt = "A simple test image of a blue sky";

    const result = await this.generateImage({
      prompt: testPrompt,
      model: 'fal-ai/flux/schnell', // Use fast model for testing
      numImages: 1
    });

    if (result.success) {
      return {
        success: true,
        images: result.images
      };
    }

    return result;
  }

  // Get available models
  getAvailableModels(): string[] {
    return Object.keys(this.models);
  }

  // Get model configuration
  getModelInfo(model: string) {
    return this.models[model as keyof typeof this.models] || null;
  }
}

// Export singleton instance
export const falaiService = new FalAIService();
export default falaiService;
